
import formatDateBasedTimezoneValue from "@/app/infrastructures/misc/common-library/FormatDateBasedTimezoneValue";
import {
  formatDateNumber,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { ManagementLoginApprovalController } from "@/app/ui/controllers/ManagementLoginApprovalController";
import { ModalMessageEntities } from "@/domain/entities/MainApp";
import { LoginApproval, LoginApprovalDevice, LoginApprovalDeviceRequest, LoginApprovalHistory, LoginApprovalHistoryRequest, LoginApprovalRequest, LoginReviewBody } from "@/domain/entities/ManagementLoginApproval";
import { Pagination } from "@/domain/entities/Pagination";
import { AxiosError } from "axios";
import { defineComponent, onMounted, ref, watch } from "vue";
import ApprovalResultDefault from "./approval-result-default.vue";
import ApprovalResultNotFound from "./approval-result-not-found.vue";
import Timer from "./timer.vue";

export default defineComponent({
  name: "ManagementLoginApproval",
  components: {
    ApprovalResultDefault,
    ApprovalResultNotFound,
    Timer
  },
  setup() {
    // --- search --- ///
    const search = ref("");
    const onSearch = (value: string) => {
      search.value = value;
    };
    const clearSearch = () => {
      search.value = "";
    };

    // --- date --- //
    const startDate = ref("");
    const endDate = ref("");

    const getFormattedDate = (date?: string | Date) => {
      const d = date ? new Date(date) : new Date();
      return formatDateNumber(d);
    };

    const setDateRange = (value: Array<string>) => {
      startDate.value = getFormattedDate(value[0]);
      endDate.value = getFormattedDate(value[1]);
    };

    // --- data --- //
    const pagination = ref(new Pagination(1, 10));
    const isLoading = ref(false);
    const errorCause = ref("");
    const loginApprovalList = ref<LoginApproval[]>([]);

   
    const fetchLoginApproval = () => {
      isLoading.value = true
      errorCause.value = "";
      const params = {
        query: search.value,
        page: pagination.value.page,
        limit: pagination.value.limit,
        startDate: startDate.value,
        endDate: endDate.value
      }

      ManagementLoginApprovalController.getLoginRequests(
        new LoginApprovalRequest(params)
      )
        .then((res) => {
          loginApprovalList.value = res.data;
          pagination.value = res.pagination;
          ManagementLoginApprovalController.setRefreshList(false);
        })
        .catch((err) => {
          errorCause.value = parsingErrorResponse(err).type;
        })
        .finally(() => {
          isLoading.value = false;
        });
    };

    // --- table modal --- //
    const confirmRequestById = async (item: LoginApproval, reviewType: "approve" | "reject") => {
      try {
        MainAppController.showLoading();
        await ManagementLoginApprovalController.postLoginApprovalReview(new LoginReviewBody({
          deviceApprovalId: item.deviceApprovalId,
          reviewType: reviewType,
          deviceIdApproval: item.deviceIdApproval
          
        })).then((response) => {
          const errorMessageId = response.data.message;
          const msg = errorMessageId.toLowerCase();

          const isSelf = msg.includes("sudah");
          const isExternal = msg.includes("telah");
          const isAccepted = msg.includes("diterima");

          if (isSelf) {
            const message = `
             <div style="color: ${reviewType === 'approve' ? '#00875A' : '#E40101'}">
               <b>${reviewType === 'approve' ? 'Diterima' : 'Ditolak'}</b><br />
               Permintaan login <b>${item.accountName}</b> telah Anda ${reviewType === 'approve' ? 'terima' : 'tolak'}.
             </div>
            `
           
            MainAppController.setSnackbarMessage(message);
            MainAppController.setSnackbarPositivity(true);
            MainAppController.setSnackbarTimeout(5000);
            MainAppController.setSnackbarBackgroundColor(reviewType === 'approve' ? "#E3FCEF" : "#FEE8E8");
            MainAppController.setSnackbarIconLeft(reviewType === 'approve' ? "success-status" : "failed-status");
            MainAppController.setSnackbar(true);
            fetchLoginApproval()

            return
          } 
          
          if (isExternal) {
            MainAppController.showMessageModal(
              new ModalMessageEntities({
                image: isAccepted ? "action_success" : "Ill_Badge_Login_Failed",
                title: errorMessageId,
                textSuccess: "OKE",
                onSubmit: () => {
                  MainAppController.closeMessageModal();
                  fetchLoginApproval()
                }
              })
            );
            
            return
          }
        });
      } catch (error) {
        const err = error as AxiosError<any>;
        MainAppController.showMessageModal(
          new ModalMessageEntities({
            image: "new-badge-failed",
            title: "Gagal diproses",
            message: err.response?.data?.message?.id || error,
            textSuccess: "Coba lagi",
            textCancel: "Kembali",
            onClose: () => {
              fetchLoginApproval();
              MainAppController.closeMessageModal();
            },
            onSubmit: () => {
              fetchLoginApproval();
              MainAppController.closeMessageModal();
            }
          })
        );
      } finally {
        MainAppController.closeLoading();
      }
    }


  

    const handleAccept = (item: LoginApproval) => {
      MainAppController.showMessageModal(new ModalMessageEntities({
        image: "badge-confirmation-general",
        title: "Terima permintaan login?",
        message: "Ketika permintaan login diterima user akan memiliki akses ke genesis.",
        textSuccess: "Ya, Terima",
        textCancel: "Batalkan",
        onClose: () => {
          MainAppController.closeMessageModal();
          fetchLoginApproval();
        },
        onSubmit: () => {
          MainAppController.closeMessageModal();
          confirmRequestById(item, "approve");
        }
      }));
    };

    const handleReject = (item: LoginApproval) => {
      MainAppController.showMessageModal(new ModalMessageEntities({
        image: "badge-confirmation-general",
        title: "Tolak permintaan login?",
        message: "Apakah kamu yakin ingin tolak permintaan login untuk user ini?",
        textSuccess: "Ya, Tolak",
        textCancel: "Batalkan",
        onClose: () => MainAppController.closeMessageModal(),
        onSubmit: () => {
          MainAppController.closeMessageModal();
          confirmRequestById(item, "reject");
        }
      }));
    };

    // --- table --- //
    const columns = [
      {
        name: "No",
        styleHead: "w-10 text-left whitespace-no-wrap text-sm font-bold text-black-lp-200",
        render: (item: any, index: number) => {
          return `<div class='text-left text-sm text-black-lp-200 font-normal flex justify-center break-words line-clamp-2'>${index +
            1 +
            pagination.value.limit * (pagination.value.page - 1)}</div>`;
        }
      },
      {
        name: "Nama",
        styleHead: "w-32 text-left whitespace-no-wrap text-sm font-bold",
        render: (item: any) => {
          return `
          <div class="flex flex-col gap-1">
            <div class="overflow-ellipsis text-sm text-black-lp-200 font-normal break-words line-clamp-2">${item.accountName}</div>
            <div class="overflow-ellipsis text-sm text-gray-lp-600 font-normal break-words line-clamp-2">${item.roleName}</div>
          </div>`;
        }
      },
      {
        name: "Nama device",
        styleHead: "w-32 text-left whitespace-no-wrap text-sm font-bold",
        render: (item: any) => {
          return `
          <div class="flex flex-col gap-1">
            <div class="overflow-ellipsis text-sm text-black-lp-200 font-normal break-words line-clamp-2">${item.deviceName}</div>
            <div class="overflow-ellipsis text-sm text-gray-lp-600 font-normal break-words line-clamp-2">${item.deviceOs}</div>
          </div>`;
        }
      },
      {
        name: "Sisa waktu expired",
        styleHead: "w-32 text-left whitespace-no-wrap text-sm font-bold",
        "custom-slot": true,
        render: () => {
          return ``;
        }
      },
      {
        name: "Tanggal permintaan",
        styleHead: "w-32 text-left whitespace-no-wrap text-sm font-bold",
        render: (item: any) => {
          return `<div class="overflow-ellipsis text-sm text-black-lp-200 font-normal break-words line-clamp-2">
          ${formatDateBasedTimezoneValue(item.createdAt, "DD MMMM YYYY, HH:mm")}</div>`;
        }
      },
      {
        name: "Kode",
        styleHead: "w-18 text-left whitespace-no-wrap text-sm font-bold",
        render: (item: any) => {
          return `<div class="overflow-ellipsis text-sm text-black-lp-200 font-normal break-words line-clamp-2">${item.pairingCode}</div>`;
        }
      },
      {
        name: "Atur",
        styleHead: "w-32 text-left whitespace-no-wrap text-sm font-bold",
        key: "double_button_column",
        styleButton: (item: LoginApproval) => ({
          outline: false,
          icon: "icon-x-white",
          sizeIcon: "18px",
          textColor: "white",
          color: "red-lp-100",
          title: "Tolak",
          styleButton: "flex flex-row-reverse",
          clickFunction: () => handleReject(item),
        }),
        styleButtonTwo: (item: LoginApproval) => ({
          outline: false,
          icon: "icon-check-white",
          sizeIcon: "18px",
          textColor: "white",
          color: "green-lp-400",
          title: "Terima",
          styleButton: "flex flex-row-reverse",
          clickFunction: () => handleAccept(item),
        })
      },
    ];

    onMounted(() => {
      fetchLoginApproval()
    });

    // executing fetching data
    watch(
      () => ManagementLoginApprovalController.refreshList,
      (newVal) => {
        if (newVal) {
          pagination.value.page = 1;
          fetchLoginApproval();
        }
      }
    );

    watch(
      [search, startDate, endDate],
      () => {
        pagination.value.page = 1;
        fetchLoginApproval();
      }
    );

    return {
      // --- search --- //
      search,
      onSearch,
      clearSearch,

      // --- date --- //
      startDate,
      endDate,
      setDateRange,
      getFormattedDate,

      // --- data --- //
      loginApprovalList,

      // --- table --- //
      columns,
      pagination,
      isLoading,
      errorCause,

      // --- modal --- //
      handleAccept,
      handleReject,
      fetchLoginApproval
    };
  }
});
